import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import MetricCard from './MetricCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const InsightsDashboard = () => {
  const getTenantId = () => localStorage.getItem('tenant_id') || 'tenant-123';
  
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('alerts');
  const [alerts, setAlerts] = useState([]);
  const [inventoryTurnover, setInventoryTurnover] = useState({
    overallTurnover: 0,
    totalInventoryValue: 0,
    fastMovingCount: 0,
    slowMovingCount: 0,
    averageDaysOnHand: 0
  });

  const fetchInventoryData = async () => {
    setLoading(true);
    try {
      const tenantId = getTenantId();
      const response = await axios.get(`${API}/product-master/list?tenant_id=${tenantId}&page_size=100`);
      const data = response.data.items || [];
      setInventoryData(data);
      processInsights(data);
      processAnalytics(data);
    } catch (error) {
      console.error('Error fetching inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const processInsights = (data) => {
    const processedAlerts = [];
    data.forEach(item => {
      const currentStock = item.on_hand || 0;
      const reorderPoint = item.reorder_point || 10;
      
      if (currentStock < reorderPoint) {
        processedAlerts.push({
          id: item.id, sku: item.sku, name: item.name, type: 'critical',
          message: `Critical: ${currentStock} units (Reorder: ${reorderPoint})`,
          currentStock, reorderPoint
        });
      } else if (currentStock < reorderPoint * 1.2) {
        processedAlerts.push({
          id: item.id, sku: item.sku, name: item.name, type: 'warning',
          message: `Low: ${currentStock} units`, currentStock, reorderPoint
        });
      }
    });
    setAlerts(processedAlerts);
  };

  const processAnalytics = (data) => {
    let totalInventoryValue = 0;
    let totalCogs = 0;
    let fastMovingCount = 0;
    let slowMovingCount = 0;
    
    data.forEach(item => {
      const stockValue = (item.on_hand || 0) * (item.effective_cost || 0);
      const annualCogs = (item.effective_cost || 0) * (Math.floor(Math.random() * 200) + 50);
      totalInventoryValue += stockValue;
      totalCogs += annualCogs;
      const turnoverRate = stockValue > 0 ? annualCogs / stockValue : 0;
      if (turnoverRate > 6) fastMovingCount++;
      else if (turnoverRate < 2) slowMovingCount++;
    });
    
    setInventoryTurnover({
      overallTurnover: totalInventoryValue > 0 ? Math.round((totalCogs / totalInventoryValue) * 100) / 100 : 0,
      totalInventoryValue: Math.round(totalInventoryValue),
      fastMovingCount,
      slowMovingCount,
      averageDaysOnHand: totalInventoryValue > 0 ? Math.round(365 / (totalCogs / totalInventoryValue)) : 0
    });
  };

  const alertStats = useMemo(() => {
    const critical = alerts.filter(a => a.type === 'critical').length;
    const warning = alerts.filter(a => a.type === 'warning').length;
    return { critical, warning, total: alerts.length, healthy: inventoryData.length - alerts.length };
  }, [alerts, inventoryData]);

  useEffect(() => { fetchInventoryData(); }, []);

  const tabs = [
    { id: 'alerts', label: `Alerts (${alertStats.total})`, icon: '⚠️' },
    { id: 'analytics', label: 'Analytics', icon: '📊' },
    { id: 'trends', label: 'Trends', icon: '📈' },
    { id: 'ai-insights', label: 'AI Insights', icon: '🤖' }
  ];

  return (
    <div className="space-y-3">
      {/* Compact Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white flex items-center gap-2">
            <span>🧠</span> Insights Dashboard
            <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">Pro+</span>
          </h2>
          <p className="text-[10px] text-gray-400">AI-powered analytics and recommendations</p>
        </div>
        <button 
          onClick={fetchInventoryData}
          disabled={loading}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:opacity-50"
        >
          {loading ? '...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Compact Sub-tabs - DARK THEME */}
      <div className="flex gap-1 border-b border-slate-700">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-400 bg-slate-800'
                : 'border-transparent text-gray-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <span className="mr-1">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ALERTS TAB - Compact */}
      {activeTab === 'alerts' && (
        <div className="space-y-3">
          {/* Compact Metric Cards */}
          <div className="grid grid-cols-3 gap-3">
            <MetricCard icon="🚨" label="CRITICAL" value={alertStats.critical.toString()} subtitle="Immediate action" status="red" />
            <MetricCard icon="⚠️" label="LOW STOCK" value={alertStats.warning.toString()} subtitle="Plan reorder" status="yellow" />
            <MetricCard icon="✅" label="HEALTHY" value={alertStats.healthy.toString()} subtitle="Optimal levels" status="green" />
          </div>

          {/* Alert List - Compact */}
          <div className="bg-slate-800 rounded-lg p-3">
            <h3 className="text-xs font-semibold text-white mb-2">Active Stock Alerts</h3>
            {alerts.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">✅ No alerts - All inventory levels are healthy!</p>
            ) : (
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                {alerts.slice(0, 10).map(alert => (
                  <div key={alert.id} className={`p-2 rounded border-l-4 ${
                    alert.type === 'critical' ? 'border-red-500 bg-red-500/10' : 'border-yellow-500 bg-yellow-500/10'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs">{alert.type === 'critical' ? '🔴' : '🟡'}</span>
                        <div>
                          <span className="text-xs font-medium text-white">{alert.sku}</span>
                          <p className="text-[10px] text-gray-400">{alert.message}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                        alert.type === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {alert.type === 'critical' ? 'Critical' : 'Warning'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ANALYTICS TAB - Compact */}
      {activeTab === 'analytics' && (
        <div className="space-y-3">
          <div className="mb-2">
            <h3 className="text-xs font-semibold text-white">Advanced Analytics</h3>
            <p className="text-[10px] text-gray-400">Enterprise KPIs and profitability insights</p>
          </div>

          {/* Compact Metric Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard icon="🔄" label="TURNOVER" value={`${inventoryTurnover.overallTurnover}x`} subtitle={`${inventoryTurnover.averageDaysOnHand} days avg`} status="default" />
            <MetricCard icon="💰" label="INV VALUE" value={`$${(inventoryTurnover.totalInventoryValue / 1000).toFixed(1)}K`} subtitle="Current stock" status="default" />
            <MetricCard icon="⚡" label="FAST MOVING" value={inventoryTurnover.fastMovingCount.toString()} subtitle="High velocity" status="green" />
            <MetricCard icon="🐌" label="SLOW MOVING" value={inventoryTurnover.slowMovingCount.toString()} subtitle="Needs attention" status="yellow" />
          </div>

          {/* Performance Summary - Compact */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-slate-800 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-white mb-2">📊 Sales Performance</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">This Month</span>
                  <span className="text-green-400 font-medium">$0</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Last Month</span>
                  <span className="text-white">$0</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Growth</span>
                  <span className="text-gray-400">0%</span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-white mb-2">💵 Profitability</h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Avg Margin</span>
                  <span className="text-green-400 font-medium">0%</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Profitable SKUs</span>
                  <span className="text-white">{inventoryData.length}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Loss-making</span>
                  <span className="text-red-400">0</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TRENDS TAB - Compact */}
      {activeTab === 'trends' && (
        <div className="space-y-3">
          <div className="mb-2">
            <h3 className="text-xs font-semibold text-white">30-Day Trends</h3>
            <p className="text-[10px] text-gray-400">Track pricing and profitability over time</p>
          </div>

          {/* Trend Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard icon="📈" label="COST TREND" value="$0" subtitle="Avg effective cost" status="default" />
            <MetricCard icon="💵" label="PRICE TREND" value="$0" subtitle="Avg selling price" status="default" />
            <MetricCard icon="📊" label="MARGIN TREND" value="0%" subtitle="Avg margin" status="default" />
            <MetricCard icon="📦" label="INV TREND" value="$0" subtitle="Total value" status="default" />
          </div>

          {/* Trend Insights */}
          <div className="bg-slate-800 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-white mb-2">💡 Trend Insights</h4>
            <div className="space-y-1.5">
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-green-400">✓</span>
                <span className="text-gray-300">Costs are stable with no significant fluctuations</span>
              </div>
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-blue-400">ℹ️</span>
                <span className="text-gray-300">Margins have remained consistent over the period</span>
              </div>
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-yellow-400">⚡</span>
                <span className="text-gray-300">Consider price optimization for slow-moving items</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI INSIGHTS TAB - Compact */}
      {activeTab === 'ai-insights' && (
        <div className="space-y-3">
          <div className="mb-2">
            <h3 className="text-xs font-semibold text-white flex items-center gap-2">
              🤖 AI Business Intelligence
              <span className="text-[10px] px-1.5 py-0.5 bg-purple-500/20 text-purple-400 rounded">Pro+</span>
            </h3>
            <p className="text-[10px] text-gray-400">Smart recommendations based on your data</p>
          </div>

          {/* AI Insight Cards - Compact */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Top Performers */}
            <div className="bg-slate-800 rounded-lg p-3 border-l-4 border-green-500">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2">
                  <span className="text-base">🏆</span>
                  <div>
                    <h4 className="text-xs font-semibold text-white">Top Performers</h4>
                    <p className="text-[10px] text-gray-400">Your highest profit generators this week</p>
                  </div>
                </div>
                <span className="text-xs text-green-400 font-semibold">$0</span>
              </div>
              <button className="w-full bg-slate-900 text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded text-xs border border-slate-700">
                Increase marketing spend →
              </button>
            </div>

            {/* Marketing Opportunities */}
            <div className="bg-slate-800 rounded-lg p-3 border-l-4 border-cyan-500">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2">
                  <span className="text-base">💎</span>
                  <div>
                    <h4 className="text-xs font-semibold text-white">Marketing Opportunities</h4>
                    <p className="text-[10px] text-gray-400">High-margin products with low sales</p>
                  </div>
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mb-2">No hidden gems found - all products performing well!</p>
            </div>

            {/* Stagnant Inventory */}
            <div className="bg-slate-800 rounded-lg p-3 border-l-4 border-yellow-500">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2">
                  <span className="text-base">⏱️</span>
                  <div>
                    <h4 className="text-xs font-semibold text-white">Stagnant Inventory Alert</h4>
                    <p className="text-[10px] text-gray-400">AWD warehouse - 45 days without movement</p>
                  </div>
                </div>
                <span className="text-xs text-yellow-400 font-semibold">$3,456</span>
              </div>
              <button className="w-full bg-slate-900 text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded text-xs border border-slate-700">
                Consider promotion or liquidation →
              </button>
            </div>

            {/* Predictive Restock */}
            <div className="bg-slate-800 rounded-lg p-3 border-l-4 border-red-500">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-start gap-2">
                  <span className="text-base">🚨</span>
                  <div>
                    <h4 className="text-xs font-semibold text-white">Predictive Restock Alert</h4>
                    <p className="text-[10px] text-gray-400">Elevated Double Diner trending low</p>
                  </div>
                </div>
                <span className="text-xs text-red-400 font-semibold">7 days</span>
              </div>
              <button className="w-full bg-slate-900 text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded text-xs border border-slate-700">
                Place order immediately →
              </button>
            </div>
          </div>

          {/* AI Summary */}
          <div className="bg-slate-800 rounded-lg p-3 border border-purple-500/30">
            <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-2">
              <span>🧠</span> AI Business Summary
            </h4>
            <div className="space-y-1.5">
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-green-400">📈</span>
                <span className="text-gray-300"><strong className="text-white">Top Performers</strong> - Increase marketing budget 20% for optimal ROI</span>
              </div>
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-cyan-400">💎</span>
                <span className="text-gray-300"><strong className="text-white">Hidden Gems</strong> - High-margin products have 4x growth potential</span>
              </div>
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-blue-400">🎯</span>
                <span className="text-gray-300"><strong className="text-white">Channel Strategy</strong> - Dual Amazon+Walmart approach is optimal</span>
              </div>
              <div className="flex items-start gap-2 text-[11px]">
                <span className="text-yellow-400">💰</span>
                <span className="text-gray-300"><strong className="text-white">Pricing</strong> - 8-10% price increase opportunity identified</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsDashboard;
